import { useEffect, useRef } from 'react';

/**
 * Un-sticks a page that thought it was gone for good.
 *
 * Setting window.location to Stripe assumes this document is on its way out, so the "Opening
 * secure payment…" state is deliberately never cleared on success. But the coach can come BACK
 * without ever touching our cancel URL: browser Back restores the document from the back/forward
 * cache with all its state intact, and in an installed PWA the checkout opens in an in-app
 * browser sheet whose Done button simply dismisses it, leaving the original page (and its
 * overlay) exactly as it was. Neither route reloads the page, so without this the spinner runs
 * forever.
 *
 * While `active`, calls `reset` as soon as the document is shown or becomes visible again.
 * `pageshow` catches the back/forward-cache restore; `visibilitychange` catches the PWA sheet
 * being dismissed. A rare false positive (the coach switching tabs in the moment between the
 * overlay appearing and the browser actually leaving) just clears the overlay early — the
 * navigation still completes, so nothing is lost.
 */
export function useReturnFromCheckout(active: boolean, reset: () => void) {
  // Latest callback in a ref so the listeners bind once per activation, not once per render.
  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    if (!active) return;
    const onPageShow = () => resetRef.current();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') resetRef.current();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [active]);
}
